/**
 * Etiqueta de produto: tamanhos suportados, layout fixo em milímetros e o
 * desenho do QR a partir da matriz (qr-code.md §14 e §15).
 *
 * O QR em si vem do middleware (`GET /produtos/qrcode?formato=matriz`): é o
 * MESMO gerador que o matching do inventário usa, então a matriz impressa é,
 * por construção, a que o backend compara com a foto. O painel só posiciona
 * os módulos na etiqueta — não escolhe versão, máscara nem correção.
 *
 * Tamanhos suportados (uma etiqueta por página, sem margem de página):
 *
 * | Etiqueta     | QR (sem margem) | Módulo | QR com margem | Distância da borda | Fonte do codPro |
 * |--------------|-----------------|--------|---------------|--------------------|-----------------|
 * | 60 × 80 mm   | 40 mm (mínimo)  | 1,6 mm | 52,8 mm       | 3,6 mm (lado/topo) | 6 mm            |
 * | 100 × 100 mm | 50 mm (ideal)   | 2 mm   | 66 mm         | 17 mm lado, 6 topo | 10 mm           |
 *
 * O 60 × 80 é o menor rolo em que o QR mínimo de 40 mm cabe com a margem
 * branca de 4 módulos dos dois lados; o 100 × 100 é o padrão, com o QR ideal
 * de 50 mm. A margem branca nunca recebe texto nem borda (§14.2).
 */

/** Versão 2 fixa (§14.2): 25 × 25 módulos em toda etiqueta. */
export const LADO_DO_SIMBOLO_EM_MODULOS = 25;

/** Zona de silêncio da norma: 4 módulos de cada lado. */
export const MARGEM_EM_MODULOS = 4;

/** §14.3: lado mínimo do QR, sem contar a margem branca. */
export const LADO_MINIMO_DO_QR_MM = 40;

/** §14.3: lado ideal do QR, sem contar a margem branca. */
export const LADO_IDEAL_DO_QR_MM = 50;

/** `codPro` é `String(14)` no Senior (§14.1). */
export const CARACTERES_MAXIMOS_DO_CODPRO = 14;

/** Avanço de cada caractere da fonte monoespaçada, em fração do corpo. */
export const AVANCO_DA_FONTE_MONO_EM = 0.6;

/** Teto de produtos numa impressão: cada um é uma ida ao middleware. */
export const LIMITE_DE_PRODUTOS = 50;

/** Teto de cópias por produto (duas faces por volume, com folga). */
export const COPIAS_MAXIMAS = 4;

export type IdDoTamanho = '60x80' | '100x100';

export type TamanhoDeEtiqueta = {
  readonly id: IdDoTamanho;
  readonly rotulo: string;
  readonly larguraMm: number;
  readonly alturaMm: number;
  /** Lado do QR SEM a margem branca. */
  readonly ladoDoQrMm: number;
  /** Distância fixa entre o topo da etiqueta e o começo da margem branca. */
  readonly topoMm: number;
  /** Corpo do `codPro`, dimensionado para caberem 14 caracteres na largura. */
  readonly fonteMm: number;
};

export const TAMANHOS_DE_ETIQUETA: readonly TamanhoDeEtiqueta[] = [
  {
    id: '60x80',
    rotulo: '60 × 80 mm — QR de 40 mm',
    larguraMm: 60,
    alturaMm: 80,
    ladoDoQrMm: LADO_MINIMO_DO_QR_MM,
    topoMm: 3.6,
    fonteMm: 6,
  },
  {
    id: '100x100',
    rotulo: '100 × 100 mm — QR de 50 mm',
    larguraMm: 100,
    alturaMm: 100,
    ladoDoQrMm: LADO_IDEAL_DO_QR_MM,
    topoMm: 6,
    fonteMm: 10,
  },
];

export const TAMANHO_PADRAO: IdDoTamanho = '100x100';

/** O tamanho pelo identificador; desconhecido cai no padrão. */
export function tamanhoDeEtiqueta(id: string): TamanhoDeEtiqueta {
  const padrao = TAMANHOS_DE_ETIQUETA.find((tamanho) => tamanho.id === TAMANHO_PADRAO);
  const achado = TAMANHOS_DE_ETIQUETA.find((tamanho) => tamanho.id === id);

  // `padrao` existe por construção da tabela acima.
  return achado ?? (padrao as TamanhoDeEtiqueta);
}

export type LayoutDaEtiqueta = {
  readonly larguraMm: number;
  readonly alturaMm: number;
  readonly moduloMm: number;
  /** Lado do símbolo com a margem branca dos dois lados. */
  readonly ladoComMargemMm: number;
  /** Canto superior esquerdo da MARGEM branca, não dos módulos. */
  readonly simbolo: { readonly xMm: number; readonly yMm: number };
  readonly texto: {
    /** Onde a área do texto começa: logo abaixo da margem branca. */
    readonly topoMm: number;
    readonly centroMm: number;
    readonly linhaDeBaseMm: number;
    readonly fonteMm: number;
  };
};

/** Arredonda ao milésimo de milímetro: `1,6 × 33` não vira `52,800000000000004`. */
function mm(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

/**
 * Posição de cada elemento na etiqueta, em milímetros.
 *
 * O QR fica centralizado na largura e a uma distância FIXA do topo (§14.3:
 * sempre na mesma posição e à mesma distância da borda); o `codPro` fica
 * centralizado na faixa entre a margem branca e a borda de baixo.
 *
 * @throws {Error} se o QR ficar abaixo do mínimo ou não couber com a margem.
 */
export function layoutDaEtiqueta(tamanho: TamanhoDeEtiqueta): LayoutDaEtiqueta {
  if (tamanho.ladoDoQrMm < LADO_MINIMO_DO_QR_MM) {
    throw new Error(`O QR precisa ter pelo menos ${String(LADO_MINIMO_DO_QR_MM)} mm de lado.`);
  }

  const moduloMm = mm(tamanho.ladoDoQrMm / LADO_DO_SIMBOLO_EM_MODULOS);
  const ladoComMargemMm = mm(moduloMm * (LADO_DO_SIMBOLO_EM_MODULOS + 2 * MARGEM_EM_MODULOS));
  const xMm = mm((tamanho.larguraMm - ladoComMargemMm) / 2);
  const topoDoTexto = mm(tamanho.topoMm + ladoComMargemMm);
  const fundoDoTexto = tamanho.alturaMm - tamanho.topoMm;

  if (xMm < 0 || topoDoTexto + tamanho.fonteMm > fundoDoTexto) {
    throw new Error(`O QR com a margem branca não cabe na etiqueta ${tamanho.id}.`);
  }

  const centroDaFaixa = (topoDoTexto + fundoDoTexto) / 2;

  return {
    larguraMm: tamanho.larguraMm,
    alturaMm: tamanho.alturaMm,
    moduloMm,
    ladoComMargemMm,
    simbolo: { xMm, yMm: tamanho.topoMm },
    texto: {
      topoMm: topoDoTexto,
      centroMm: mm(tamanho.larguraMm / 2),
      // A altura das maiúsculas é ~0,7 do corpo: a linha de base desce
      // metade disso para o texto ficar visualmente no meio da faixa.
      linhaDeBaseMm: mm(centroDaFaixa + tamanho.fonteMm * 0.35),
      fonteMm: tamanho.fonteMm,
    },
  };
}

/** A página de impressão tem o tamanho exato da etiqueta, sem margem. */
export function regraDaPagina(tamanho: TamanhoDeEtiqueta): string {
  return `@page { size: ${String(tamanho.larguraMm)}mm ${String(tamanho.alturaMm)}mm; margin: 0; }`;
}

const ASCII_IMPRIMIVEL = /^[\x20-\x7e]+$/;

/**
 * Motivo da recusa do `codPro`, ou `null` quando ele segue a §14.1.
 *
 * Espelho da `validarCodPro` do middleware: recusar aqui poupa uma ida ao
 * servidor e diz o motivo, que o 400 da API não carrega no corpo.
 */
export function validarCodPro(codigo: string): string | null {
  if (codigo.length === 0) {
    return 'Código vazio.';
  }
  if (codigo.length > CARACTERES_MAXIMOS_DO_CODPRO) {
    return `Mais de ${String(CARACTERES_MAXIMOS_DO_CODPRO)} caracteres.`;
  }
  if (!ASCII_IMPRIMIVEL.test(codigo)) {
    return 'Caractere fora do padrão: só ASCII imprimível, sem acento.';
  }
  if (codigo !== codigo.trim()) {
    return 'Espaço no começo ou no fim.';
  }
  return null;
}

export type Recusa = { readonly codigo: string; readonly motivo: string };

export type Acrescimo = {
  readonly codigos: readonly string[];
  readonly recusados: readonly Recusa[];
};

/**
 * Junta à lista os códigos digitados, um por linha.
 *
 * As pontas são aparadas — planilha colada traz espaço e tabulação, e
 * espaço na ponta nunca faz parte de um `codPro` válido. Repetido não entra
 * duas vezes (a quantidade é o campo de cópias), e a ordem é a de chegada.
 */
export function acrescentarCodigos(atuais: readonly string[], texto: string): Acrescimo {
  const linhas = texto
    .split(/\r?\n/)
    .map((linha) => linha.trim())
    .filter((linha) => linha.length > 0);

  return linhas.reduce<Acrescimo>(
    (acumulado, codigo) => {
      if (acumulado.codigos.includes(codigo)) {
        return acumulado;
      }

      const motivo =
        validarCodPro(codigo) ??
        (acumulado.codigos.length >= LIMITE_DE_PRODUTOS
          ? `Acima de ${String(LIMITE_DE_PRODUTOS)} produtos por impressão.`
          : null);

      return motivo === null
        ? { ...acumulado, codigos: [...acumulado.codigos, codigo] }
        : { ...acumulado, recusados: [...acumulado.recusados, { codigo, motivo }] };
    },
    { codigos: [...atuais], recusados: [] },
  );
}

/**
 * Caminho SVG dos módulos escuros, em unidade de módulo e SEM a margem.
 *
 * Cada sequência de módulos escuros de uma linha vira um retângulo só: o
 * arquivo fica menor e não aparece fresta entre módulos vizinhos na
 * impressão.
 *
 * @throws {Error} se `modulos` não tiver `n × n` caracteres.
 */
export function caminhoDaMatriz(modulos: string, n: number): string {
  if (modulos.length !== n * n) {
    throw new Error(`A matriz precisa de ${String(n * n)} módulos.`);
  }

  const partes: string[] = [];

  for (let linha = 0; linha < n; linha += 1) {
    let inicio = -1;

    for (let coluna = 0; coluna <= n; coluna += 1) {
      const escuro = coluna < n && modulos[linha * n + coluna] === '1';

      if (escuro && inicio < 0) {
        inicio = coluna;
      } else if (!escuro && inicio >= 0) {
        const largura = String(coluna - inicio);
        partes.push(`M${String(inicio)} ${String(linha)}h${largura}v1h-${largura}z`);
        inicio = -1;
      }
    }
  }

  return partes.join('');
}

export type EtiquetaParaImprimir = { readonly chave: string; readonly codPro: string };

/** Cada produto repetido pelas cópias, na ordem da lista. */
export function etiquetasParaImprimir(
  codigos: readonly string[],
  copias: number,
): readonly EtiquetaParaImprimir[] {
  const vezes = Math.min(COPIAS_MAXIMAS, Math.max(1, Math.trunc(copias)));

  return codigos.flatMap((codPro) =>
    Array.from({ length: vezes }, (_, copia) => ({ chave: `${codPro}#${String(copia)}`, codPro })),
  );
}
